import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Alert,
  AlertBody,
  AlertIcon,
  Button,
  Checkbox,
  cn,
  ConfirmationDialog,
  Loader,
  Pagination,
  RiCloseLine,
  RiErrorWarningLine,
  RiFileUploadLine,
  RiInformationLine,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'component-library';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useGetDraftPlotGroups, usePostDraftPlot } from '@/api/rest/resources/plot';
import { DraftPlotGroup, PlotType } from '@/api/rest/resources/types/plot';
import { PlotTypeIcon, withSuspenseBoundary } from '@/components';
import { useOrganization } from '@/hooks/useOrganization';
import { Logger } from '@/lib/logs/logger';
import { isRestApiServerError } from '@/utils/useRestApiServerErrorHandling';

import { CommitConfirmationDialog } from './components/CommitConfirmationDialog';
import { DraftPlotGroupsTable } from './components/DraftPlotGroupsTable';
import { DraftPlotsPageLayout } from './components/DraftPlotsPageLayout';
import { DraftPlotsReviewFooter } from './components/DraftPlotsReviewFooter';
import { EditPlotPanel } from './components/EditPlotPanel';
import { GroupEditPanel } from './components/GroupEditPanel';
import { NewGroupPanel } from './components/NewGroupPanel';
import { PAGE_SIZE, PLOT_LAND_TYPES } from './constants';
import { useDraftPlotBulkActions } from './hooks/useDraftPlotBulkActions';
import { useDraftPlotsPanel } from './hooks/useDraftPlotsPanel';
import { useDraftPlotsTableColumns } from './hooks/useDraftPlotsTableColumns';
import { usePlotPaths } from './hooks/usePlotPaths';
import { useShapefileDropzone } from './hooks/useShapefileDropzone';
import { LandlerField } from './utils';

type PanelState = 'idle' | 'configure' | 'uploading' | 'review' | 'editPlot' | 'editGroup' | 'addGroup';

type ActivePanel = 'editGroup' | 'addGroup' | 'editPlot' | 'main';

function resolveActivePanel(
  panelState: PanelState,
  selectedGroupId: string | null,
  selectedPlotId: string | null,
): ActivePanel {
  if (panelState === 'editGroup' && selectedGroupId) return 'editGroup';
  if (panelState === 'addGroup') return 'addGroup';
  if (panelState === 'editPlot' && selectedPlotId) return 'editPlot';
  return 'main';
}

const UploadPlotsPageInner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const cancelLabel = t('global.ui.buttons.cancel');
  const { plots } = usePlotPaths();

  const initialDraftPlotId = location.state?.draftPlotId ?? null;

  const [panelState, setPanelState] = useState<PanelState>('idle');
  const [selectedPlotId, setSelectedPlotId] = useState<string | null>(initialDraftPlotId);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [selectedLandType, setSelectedLandType] = useState<PlotType | ''>('');
  const [autoFixHoles, setAutoFixHoles] = useState(false);
  const [uploadError, setUploadError] = useState<string | undefined>();
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
  const [isCommitConfirmOpen, setIsCommitConfirmOpen] = useState(false);
  const [skipIdleAutoReview, setSkipIdleAutoReview] = useState(false);
  const [fixedPolygonsResult, setFixedPolygonsResult] = useState<{ fixed: number; total: number } | null>(null);

  const activePanel = resolveActivePanel(panelState, selectedGroupId, selectedPlotId);

  // Deep-linking to a specific draft (e.g. from the org-wide accordion) needs org-wide scope,
  // since that draft may belong to a different staff member than the one reviewing it here
  const {
    organizationId,
    allDraftPlots,
    filteredPlots,
    refetchDraftPlots,
    panelWidth,
    mapRef,
    setMapLoaded,
    hoveredPlot,
    hoveredCentroid,
    draftPlotsGeoJSON,
    draftPlotCentroidsGeoJSON,
    rowSelection,
    setRowSelection,
    sorting,
    setSorting,
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    handleCommit,
    handleSaveDraftPlots,
    canCommit,
    isCommitting,
    handleDragHandleMouseDown,
    setHoveredPlotId,
    isPanelCollapsed,
    setIsPanelCollapsed,
  } = useDraftPlotsPanel(panelState, {
    allOrgDrafts: initialDraftPlotId !== null,
    focusedPlotId: selectedPlotId,
  });

  const organization = useOrganization({ id: organizationId }).data.results[0];

  const { data: groupsData, refetch: refetchGroups } = useGetDraftPlotGroups({
    queryParams: { organization: organizationId ?? undefined },
  });
  const allGroups = useMemo<DraftPlotGroup[]>(() => groupsData?.results ?? [], [groupsData?.results]);
  const groupedPlotIds = useMemo(() => new Set(allGroups.flatMap((group) => group.draft_plot_ids)), [allGroups]);
  const hasUngroupedPlots = useMemo(
    () => allDraftPlots.length === 0 || allDraftPlots.some((plot) => !groupedPlotIds.has(plot.id)),
    [allDraftPlots, groupedPlotIds],
  );
  const ungroupedFilteredPlots = useMemo(
    () => filteredPlots.filter((plot) => !groupedPlotIds.has(plot.id)),
    [filteredPlots, groupedPlotIds],
  );
  const filteredGroups = useMemo(
    () =>
      searchTerm ? allGroups.filter((group) => group.name.toLowerCase().includes(searchTerm.toLowerCase())) : allGroups,
    [allGroups, searchTerm],
  );
  const cropTypeMissingCount = useMemo(
    () => allDraftPlots.filter((plot) => plot.land_type === PlotType.CROPLAND && plot.crops.length === 0).length,
    [allDraftPlots],
  );

  const { handleDeleteOne, handleDiscardAll, isDiscarding } = useDraftPlotBulkActions({
    organizationId,
    remainingPlotCount: allDraftPlots.length,
    onEmpty: () => setPanelState('idle'),
  });

  const tableColumns = useDraftPlotsTableColumns(handleDeleteOne);

  // An empty selection submits every draft plot, matching the existing "Save draft plots" fallback behavior.
  const selectedPlotIds = Object.entries(rowSelection)
    .filter(([, v]) => v)
    .map(([k]) => k);
  const plotsToCommit =
    selectedPlotIds.length > 0 ? allDraftPlots.filter((plot) => selectedPlotIds.includes(plot.id)) : allDraftPlots;

  const { mutateAsync: uploadFile, progress, status: uploadStatus } = usePostDraftPlot();

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    fileErrors,
    isAnalyzing,
    pendingFile,
    detectedColumns,
    columnMappings,
    setColumnMappings,
    landlerFields,
  } = useShapefileDropzone({
    disabled: panelState !== 'idle',
    t,
    onDropAccepted: () => {
      setSkipIdleAutoReview(false);
      setAutoFixHoles(false);
      setPanelState('configure');
    },
  });

  const isKmlOrKmzFile = /\.(kml|kmz)$/i.test(pendingFile?.name ?? '');

  // Transition from idle to review (or straight to editPlot for a deep-linked draft) once draft plots are available
  useEffect(() => {
    if (panelState === 'idle' && allDraftPlots.length > 0 && !skipIdleAutoReview) {
      const hasSelectedPlot = allDraftPlots.some((plot) => plot.id === selectedPlotId);
      setPanelState(hasSelectedPlot ? 'editPlot' : 'review');
    }
  }, [panelState, allDraftPlots, selectedPlotId, skipIdleAutoReview]);

  const handleUpload = async () => {
    if (!pendingFile || !selectedLandType) return;
    setPanelState('uploading');
    setUploadError(undefined);
    const landStewardNameColumn = [...columnMappings].find(([, field]) => field === 'land_steward_name')?.[0];
    const labelColumns = [...columnMappings].filter(([, field]) => field === 'label').map(([column]) => column);
    try {
      const result = await uploadFile({
        bodyData: {
          file: pendingFile,
          organization: organizationId,
          land_type: selectedLandType as PlotType,
          ...(landStewardNameColumn && { land_steward_name_column: landStewardNameColumn }),
          ...(labelColumns.length > 0 && { label_columns: labelColumns }),
          ...(isKmlOrKmzFile && autoFixHoles && { auto_fix_holes: true }),
        },
      });
      setFixedPolygonsResult(
        result?.fixed_polygons !== undefined && result?.total_polygons !== undefined
          ? { fixed: result.fixed_polygons, total: result.total_polygons }
          : null,
      );
      await refetchDraftPlots();
      setPanelState('review');
    } catch (error) {
      let message = t('shared.uploadPlots.error.unknown');
      if (isRestApiServerError(error) && error.formErrors[0]?.messages[0]) {
        [message] = error.formErrors[0].messages;
      }
      setUploadError(message);
      setPanelState('configure');
      Logger.errorOnce(error as Error);
    }
  };

  const table = useReactTable({
    data: ungroupedFilteredPlots,
    columns: tableColumns,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    initialState: { pagination: { pageIndex: 0, pageSize: PAGE_SIZE } },
    state: { sorting, rowSelection },
  });

  const pageStart = table.getState().pagination.pageIndex * PAGE_SIZE;
  const pageEnd = pageStart + PAGE_SIZE;
  const isFormComplete = !!selectedLandType;
  const editingPlot = allDraftPlots.find((p) => p.id === selectedPlotId) ?? null;
  const editingGroup = allGroups.find((g) => g.id === selectedGroupId) ?? null;

  const renderFooterLeftAction = () => {
    if (panelState === 'idle') {
      return (
        <Button variant='outline' onClick={() => navigate(-1)}>
          {t('global.ui.buttons.back')}
        </Button>
      );
    }

    return (
      <Button
        variant='outline'
        onClick={() => {
          if (panelState === 'configure') setPanelState('idle');
          else navigate(-1);
        }}
      >
        {cancelLabel}
      </Button>
    );
  };

  return (
    <DraftPlotsPageLayout
      data-testid='upload-plots-page'
      panelWidth={panelWidth}
      isPanelCollapsed={isPanelCollapsed}
      onDragHandleMouseDown={handleDragHandleMouseDown}
      mapRef={mapRef}
      allDraftPlots={allDraftPlots}
      draftPlotsGeoJSON={draftPlotsGeoJSON}
      draftPlotCentroidsGeoJSON={draftPlotCentroidsGeoJSON}
      hoveredPlot={hoveredPlot}
      hoveredCentroid={hoveredCentroid}
      onMapLoad={() => setMapLoaded(true)}
      setHoveredPlotId={setHoveredPlotId}
    >
      {activePanel === 'editGroup' && editingGroup && (
        <GroupEditPanel
          group={editingGroup}
          plots={allDraftPlots}
          organizationId={organizationId ?? ''}
          onBack={() => {
            setSelectedGroupId(null);
            setPanelState('review');
          }}
          onDeleted={async () => {
            setSelectedGroupId(null);
            setPanelState('review');
            await Promise.all([refetchDraftPlots(), refetchGroups()]);
          }}
          onSaved={async () => {
            setSelectedGroupId(null);
            setPanelState('review');
            await refetchGroups();
          }}
          onRefetchGroups={refetchGroups}
          onEditPlot={(id) => {
            setSelectedPlotId(id);
            setPanelState('editPlot');
          }}
        />
      )}
      {activePanel === 'addGroup' && (
        <NewGroupPanel
          plots={allDraftPlots.filter((p) => rowSelection[p.id])}
          organizationId={organizationId ?? ''}
          onBack={() => setPanelState('review')}
          onSaved={async () => {
            setRowSelection({});
            setPanelState('review');
            await Promise.all([refetchDraftPlots(), refetchGroups()]);
          }}
        />
      )}
      {activePanel === 'editPlot' && editingPlot && (
        <EditPlotPanel
          plot={editingPlot}
          organizationId={organizationId ?? ''}
          onBack={() => {
            setSelectedPlotId(null);
            setPanelState(selectedGroupId ? 'editGroup' : 'review');
          }}
          onDelete={(id) => {
            handleDeleteOne(id);
            setSelectedPlotId(null);
            setPanelState(selectedGroupId ? 'editGroup' : 'review');
          }}
          onSaved={() => {
            setPanelState(selectedGroupId ? 'editGroup' : 'review');
            setSelectedPlotId(null);
            refetchDraftPlots();
          }}
        />
      )}
      {activePanel === 'main' && (
        <>
          {/* Header */}
          <div className='shrink-0 border-b border-neutral-black-12 px-6 py-5'>
            <div className='flex items-center justify-between gap-4'>
              <div>
                {organization && (
                  <span className='typography-overline block text-text-secondary'>{organization.name}</span>
                )}
                <h1 className='typography-h3'>{t('shared.uploadPlots.title')}</h1>
              </div>
              <button
                type='button'
                aria-label={t('shared.uploadPlots.collapsePanelAriaLabel')}
                onClick={() => setIsPanelCollapsed(true)}
                className='shrink-0 text-text-secondary hover:text-text-primary'
              >
                <RiCloseLine size={20} />
              </button>
            </div>
          </div>

          {/* Panel body */}
          <div className='flex-1 overflow-y-auto px-6 py-5'>
            {panelState === 'idle' && (
              <Stack spacing={6}>
                <Stack spacing={2}>
                  <label className='typography-body2Semibold'>{t('shared.uploadPlots.file.label')}</label>
                  <div
                    {...getRootProps({
                      className: cn(
                        'flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed p-8 transition-colors cursor-pointer hover:border-primary-100 hover:bg-neutral-black-4',
                        { 'border-primary-100 bg-primary-12': isDragActive },
                      ),
                    })}
                  >
                    <input {...getInputProps()} />
                    <RiFileUploadLine size={32} className='text-primary-100' />
                    <Stack spacing={1} className='items-center text-center'>
                      <span className='typography-body1Semibold'>{t('shared.uploadPlots.dropzone.activeTitle')}</span>
                      <span className='typography-body2 text-text-secondary'>
                        {t('shared.uploadPlots.dropzone.subtitle')}
                      </span>
                    </Stack>
                  </div>
                </Stack>

                {fileErrors.map((err, i) => (
                  <Alert key={i} type='error'>
                    <AlertIcon>
                      <RiErrorWarningLine size={20} />
                    </AlertIcon>
                    <AlertBody>{err}</AlertBody>
                  </Alert>
                ))}
              </Stack>
            )}

            {panelState === 'configure' && (
              <Stack spacing={6}>
                {isAnalyzing ? (
                  <Stack spacing={2} className='items-center justify-center py-8'>
                    <Loader className='h-6 w-6' />
                    <span className='typography-body2 text-text-secondary'>
                      {t('shared.uploadPlots.configure.detecting')}
                    </span>
                  </Stack>
                ) : (
                  <Stack spacing={4}>
                    <span className='typography-overline text-text-secondary'>
                      {t('shared.uploadPlots.configure.matchColumns')}
                    </span>

                    {detectedColumns.length === 0 && (
                      <span className='typography-body2 text-text-secondary'>
                        {t('shared.uploadPlots.configure.noColumns')}
                      </span>
                    )}

                    {detectedColumns.map((col) => (
                      <div key={col} className='flex items-center gap-4'>
                        <Select
                          value={columnMappings.get(col) ?? 'none'}
                          onValueChange={(v) => setColumnMappings((prev) => new Map(prev).set(col, v as LandlerField))}
                        >
                          <SelectTrigger className='w-40' />
                          <SelectContent>
                            <SelectItem value='none'>{t('shared.uploadPlots.configure.columnNone')}</SelectItem>
                            {landlerFields.map((f) => {
                              const fieldLabel = () => {
                                if (f === 'land_steward_name') return 'Land Steward Name';
                                return `${f.charAt(0).toUpperCase()}${f.slice(1)}`;
                              };
                              return (
                                <SelectItem key={f} value={f}>
                                  {fieldLabel()}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                        <div className='flex items-center gap-2 text-sm text-text-secondary'>
                          <span className='inline-block h-4 w-4 rounded-sm border border-primary-100 bg-primary-12' />
                          {col}
                        </div>
                      </div>
                    ))}

                    <Stack spacing={2} className='border-t border-neutral-black-12 pt-4'>
                      <label className='typography-body2Semibold'>{t('shared.uploadPlots.landType.label')}</label>
                      <Select value={selectedLandType} onValueChange={(v) => setSelectedLandType(v as PlotType)}>
                        <SelectTrigger
                          placeholder={t('shared.uploadPlots.landType.placeholder')}
                          leftAdornment={
                            selectedLandType ? <PlotTypeIcon type={selectedLandType as PlotType} /> : undefined
                          }
                        />
                        <SelectContent>
                          {PLOT_LAND_TYPES.map((type) => (
                            <SelectItem key={type} value={type} leftAdornment={<PlotTypeIcon type={type} />}>
                              {t(`global.plotTypes.${type}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Stack>

                    {isKmlOrKmzFile && (
                      <Checkbox
                        id='auto-fix-holes'
                        checked={autoFixHoles}
                        onCheckedChange={(checked) => setAutoFixHoles(checked === true)}
                      >
                        <Stack spacing={0}>
                          <label htmlFor='auto-fix-holes' className='typography-body2Semibold'>
                            {t('shared.uploadPlots.configure.autoFixHoles.label')}
                          </label>
                          <span className='typography-body2 text-text-secondary'>
                            {t('shared.uploadPlots.configure.autoFixHoles.helperText')}
                          </span>
                        </Stack>
                      </Checkbox>
                    )}
                  </Stack>
                )}

                {uploadError && (
                  <Alert type='error'>
                    <AlertIcon>
                      <RiErrorWarningLine size={20} />
                    </AlertIcon>
                    <AlertBody>{uploadError}</AlertBody>
                  </Alert>
                )}
              </Stack>
            )}

            {panelState === 'uploading' && (
              <Stack spacing={4} className='items-center justify-center py-16'>
                <span className='typography-body1Semibold text-primary-100'>{t('shared.uploadPlots.uploading')}</span>
                <div role='progressbar' className='h-2 w-full rounded-full border border-outlined-border-23p'>
                  <div
                    className='h-full rounded-full bg-primary-100 transition-all'
                    style={{ width: `${uploadStatus === 'pending' ? progress : 100}%` }}
                  />
                </div>
              </Stack>
            )}

            {panelState === 'review' && (
              <Stack spacing={4}>
                {fixedPolygonsResult && fixedPolygonsResult.fixed > 0 && (
                  <Alert type='default'>
                    <AlertIcon>
                      <RiInformationLine size={20} />
                    </AlertIcon>
                    <AlertBody>
                      {t('shared.uploadPlots.review.fixedPolygonsInfo', {
                        fixed: fixedPolygonsResult.fixed,
                        total: fixedPolygonsResult.total,
                      })}
                    </AlertBody>
                  </Alert>
                )}
                {cropTypeMissingCount > 0 && (
                  <Alert type='error'>
                    <AlertIcon>
                      <RiErrorWarningLine size={20} />
                    </AlertIcon>
                    <AlertBody>
                      {t('shared.uploadPlots.review.cropTypeMissingWarning', { count: cropTypeMissingCount })}
                    </AlertBody>
                  </Alert>
                )}
                <Stack direction='row' className='items-center justify-between'>
                  <span className='typography-overline text-text-secondary'>
                    {t('shared.uploadPlots.review.plotCount', { count: allDraftPlots.length })}
                  </span>
                  <Stack direction='row' className='gap-2'>
                    <Button
                      variant='outline'
                      size='small'
                      onClick={() => {
                        setSkipIdleAutoReview(true);
                        setFixedPolygonsResult(null);
                        setPanelState('idle');
                      }}
                    >
                      {t('shared.uploadPlots.review.uploadMore')}
                    </Button>
                    <Button
                      variant='outline'
                      size='small'
                      loading={isDiscarding}
                      onClick={() => setIsDiscardDialogOpen(true)}
                    >
                      {t('shared.uploadPlots.review.discardAll')}
                    </Button>
                  </Stack>
                </Stack>
                <input
                  type='text'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('shared.uploadPlots.review.searchPlaceholder')}
                  className='w-full rounded-lg border border-neutral-black-23 px-3 py-2 text-sm outline-none focus:border-primary-100'
                />
                <DraftPlotGroupsTable
                  groups={filteredGroups}
                  onSelect={(id) => {
                    setSelectedGroupId(id);
                    setPanelState('editGroup');
                  }}
                />
                {hasUngroupedPlots && (
                  <>
                    <Stack className='flex-row items-center justify-between'>
                      <span className='typography-overline text-text-secondary'>
                        {t('shared.uploadPlots.review.paginationTitle', {
                          count: ungroupedFilteredPlots.length,
                          start: Math.max(1, pageStart + 1),
                          end: Math.min(pageEnd, ungroupedFilteredPlots.length),
                        })}
                      </span>
                      <Pagination
                        className='typography-overline text-text-secondary'
                        currentPage={currentPage}
                        totalCount={ungroupedFilteredPlots.length}
                        pageSize={PAGE_SIZE}
                        onPageChange={(nextPage) => {
                          table.setPageIndex(nextPage - 1);
                          setCurrentPage(nextPage);
                        }}
                      />
                    </Stack>
                    <div className='rounded-3xl bg-white-100'>
                      <Table data-testid='draft-plots-table'>
                        <TableHeader>
                          {table.getHeaderGroups().map((hg) => (
                            <TableRow key={hg.id}>
                              {hg.headers.map((h) => (
                                <TableHead key={h.id}>
                                  {flexRender(h.column.columnDef.header, h.getContext())}
                                </TableHead>
                              ))}
                            </TableRow>
                          ))}
                        </TableHeader>
                        <TableBody>
                          {table.getRowModel().rows.map((row) => (
                            <TableRow
                              key={row.id}
                              data-testid='draft-plot-row'
                              className='group/row cursor-pointer'
                              onClick={() => {
                                setSelectedPlotId(row.original.id);
                                setPanelState('editPlot');
                              }}
                            >
                              {row.getVisibleCells().map((cell) => (
                                <TableCell
                                  key={cell.id}
                                  // The select checkbox is small; stop the click from bubbling to the row so a
                                  // near-miss click toggles selection instead of accidentally opening the edit panel.
                                  onClick={cell.column.id === 'select' ? (e) => e.stopPropagation() : undefined}
                                >
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </>
                )}
              </Stack>
            )}
          </div>

          {/* Bottom buttons */}
          {panelState === 'review' ? (
            <DraftPlotsReviewFooter
              rowSelection={rowSelection}
              organizationId={organizationId ?? ''}
              canCommit={canCommit}
              cancelLabel={cancelLabel}
              onCancel={() => navigate(-1)}
              onSaveDraftPlots={handleSaveDraftPlots}
              onCommit={() => setIsCommitConfirmOpen(true)}
              onRefetch={refetchDraftPlots}
              onAddToGroup={() => setPanelState('addGroup')}
            />
          ) : (
            <div className='flex shrink-0 items-center justify-between border-t border-neutral-black-12 px-6 py-4'>
              {renderFooterLeftAction()}

              <Stack direction='row' className='gap-3'>
                {panelState === 'idle' && (
                  <Button variant='outline' onClick={() => navigate(`${plots}?organizationId=${organizationId}`)}>
                    {cancelLabel}
                  </Button>
                )}
                {panelState === 'configure' && (
                  <Button onClick={handleUpload} disabled={!isFormComplete}>
                    {t('global.ui.buttons.next')}
                  </Button>
                )}
              </Stack>
            </div>
          )}

          <ConfirmationDialog
            open={isDiscardDialogOpen}
            onOpenChange={setIsDiscardDialogOpen}
            header={t('shared.uploadPlots.review.discardAllConfirm.header')}
            body={t('shared.uploadPlots.review.discardAllConfirm.body')}
            CancelAction={
              <Button variant='outline' onClick={() => setIsDiscardDialogOpen(false)}>
                {cancelLabel}
              </Button>
            }
            PrimaryAction={
              <Button
                className='bg-error hover:bg-error-dark'
                loading={isDiscarding}
                onClick={() => {
                  setIsDiscardDialogOpen(false);
                  handleDiscardAll();
                }}
              >
                {t('shared.uploadPlots.review.discardAll')}
              </Button>
            }
          />

          <CommitConfirmationDialog
            open={isCommitConfirmOpen}
            onOpenChange={setIsCommitConfirmOpen}
            plotNames={plotsToCommit.map((plot) => plot.name)}
            isSubmitting={isCommitting}
            onConfirm={() => {
              setIsCommitConfirmOpen(false);
              handleCommit(plotsToCommit.map((plot) => plot.id));
            }}
          />
        </>
      )}
    </DraftPlotsPageLayout>
  );
};

export const UploadPlotsPage = withSuspenseBoundary(UploadPlotsPageInner, <Loader className='min-h-screen' />);
